import React from 'react';
import { Share, StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native';
import PageHeader from '../components/account/PageHeader';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import Text from '../components/ui/Text';
import { Booking } from '../data/bookings';
import { invoiceFor } from '../data/invoice';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/**
 * The tax invoice, as a document rather than a screen of rows.
 *
 * It is drawn as a bordered sheet with its number in the corner because that
 * is what makes it read as a *document* - something to keep, forward to an
 * employer, or attach to a claim. A list styled like the rest of the app reads
 * as another page of the app.
 *
 * ---------------------------------------------------------------------------
 * ONLY WHAT THE CUSTOMER AGREED TO
 * ---------------------------------------------------------------------------
 * Every figure here comes from the booking's own total. The platform's cut and
 * the professional's share are a commission record and never appear - see the
 * note in `data/invoice.ts`.
 *
 * ---------------------------------------------------------------------------
 * DOWNLOAD IS SHARE, FOR NOW
 * ---------------------------------------------------------------------------
 * There is no PDF: nothing generates one and there is no URL to fetch. Rather
 * than a "Download" button that fails, the control shares the invoice's own
 * details as text through the system sheet, which is genuinely useful and
 * honest about what it is. When the backend issues `invoicePdfUrl`, this
 * becomes a download and the button keeps its place.
 */

/**
 * Rupees, grouped, with paise only when there are some - `Intl` is not
 * reliably present on Android JSC. GST is rarely a round number (5% of 129 is
 * 6.45) and an invoice that rounds it no longer adds up.
 */
const rupees = (value: number) => {
  const [whole, paise] = (
    Number.isInteger(value) ? String(value) : value.toFixed(2)
  ).split('.');
  return (
    '₹' +
    whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
    (paise ? `.${paise}` : '')
  );
};

type Props = {
  booking: Booking;
  /** Whoever is signed in. See the note on `invoiceFor` - never a guess. */
  billedTo: string;
  onBack: () => void;
};

function InvoiceScreen({ booking, billedTo, onBack }: Props) {
  const resp = useResponsive();
  const invoice = invoiceFor(booking, billedTo);

  const share = () => {
    Share.share({
      title: `Homingo invoice ${invoice.number}`,
      message:
        `Homingo invoice ${invoice.number}\n` +
        `Booking ${invoice.bookingRef}\n` +
        `${invoice.issuedOn}\n\n` +
        invoice.lines
          .map(line => `${line.label}  ${rupees(line.amount)}`)
          .join('\n') +
        (invoice.tax > 0
          ? `\n\nGST (${invoice.taxPercent}%)  ${rupees(invoice.tax)}`
          : '') +
        `\n\nTotal paid ${rupees(invoice.total)} (${invoice.paidWith})`,
    }).catch(() => {});
  };

  return (
    <View style={styles.root}>
      <PageHeader title="Invoice" onBack={onBack} />

      <ScrollView
        style={styles.scroller}
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.sheet}>
          {/* --------------------------------------------------- masthead */}
          <View style={styles.masthead}>
            <View style={styles.grow}>
              <Text variant="labelSm" color={colors.inkMuted}>
                TAX INVOICE
              </Text>
              <Text variant="titleLg">{invoice.number}</Text>
            </View>
            <View style={styles.mark}>
              <Icon name="document" size={20} color={colors.primary} />
            </View>
          </View>

          <View style={styles.rule} />

          {/* ------------------------------------------------------ facts */}
          <View style={styles.facts}>
            <Fact label="Issued" value={invoice.issuedOn} />
            <Fact label="Booking" value={invoice.bookingRef} />
            <Fact label="Billed to" value={invoice.billedTo} />
            <Fact label="Service address" value={invoice.address} />
          </View>

          <View style={styles.rule} />

          {/* ------------------------------------------------------ lines */}
          {invoice.lines.map((line, i) => (
            <View key={`${line.label}-${i}`} style={styles.line}>
              <Text variant="bodySm" color={colors.inkBody} style={styles.grow}>
                {line.label}
              </Text>
              <Text variant="bodySm" color={colors.ink}>
                {line.credit ? '−' : ''}
                {rupees(line.amount)}
              </Text>
            </View>
          ))}

          <View style={styles.rule} />

          <View style={styles.line}>
            <Text variant="bodySm" color={colors.inkMuted} style={styles.grow}>
              Subtotal
            </Text>
            <Text variant="bodySm">{rupees(invoice.subtotal)}</Text>
          </View>
          {/*
            The tax line is drawn even at zero.
            An invoice that simply omits tax invites the question of whether it
            was charged; one that states ₹0 has answered it.
          */}
          <View style={styles.line}>
            <Text variant="bodySm" color={colors.inkMuted} style={styles.grow}>
              GST ({invoice.taxPercent}%)
            </Text>
            <Text variant="bodySm">{rupees(invoice.tax)}</Text>
          </View>

          <View style={styles.total}>
            <Text variant="subtitle" style={styles.grow}>
              Total paid
            </Text>
            <Text variant="title">{rupees(invoice.total)}</Text>
          </View>

          <Text variant="caption" color={colors.inkMuted}>
            Paid with {invoice.paidWith}
          </Text>

          <View style={styles.rule} />

          {/* ----------------------------------------------------- seller */}
          <View style={styles.seller}>
            <Text variant="caption" color={colors.inkBody}>
              {invoice.seller.name}
            </Text>
            {/* Each line is left off until ops has set it in Settings. */}
            {invoice.seller.address ? (
              <Text variant="caption" color={colors.inkMuted}>
                {invoice.seller.address}
              </Text>
            ) : null}
            {invoice.seller.gstin ? (
              <Text variant="caption" color={colors.inkMuted}>
                GSTIN {invoice.seller.gstin}
              </Text>
            ) : null}
            {invoice.seller.sac ? (
              <Text variant="caption" color={colors.inkMuted}>
                SAC {invoice.seller.sac}
              </Text>
            ) : null}
          </View>
        </View>

        <Button label="Share invoice" leadingIcon="share" onPress={share} />

        <View style={styles.note}>
          <Icon name="shieldCheck" size={15} color={colors.primary} />
          <Text variant="caption" color={colors.inkMuted} style={styles.grow}>
            Questions about this invoice? Raise a billing request from Help &
            Support and quote {invoice.number}.
          </Text>
        </View>
      </ScrollView>
    </View>
  );
}

/** A labelled fact in the document's header block. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.fact}>
      <Text variant="caption" color={colors.inkMuted} style={styles.factLabel}>
        {label}
      </Text>
      <Text variant="caption" color={colors.inkBody} style={styles.grow}>
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.md,
    paddingBottom: 120,
  },
  /* The document itself: paper on the canvas. */
  sheet: {
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  masthead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  mark: {
    width: 42,
    height: 42,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  rule: {
    height: hairline.borderWidth,
    backgroundColor: hairline.borderColor,
    marginVertical: spacing.sm,
  },
  facts: {
    gap: spacing.xs,
  },
  fact: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  /* Fixed so every value starts on the same column - the thing that makes a
     block of facts read as a document rather than as sentences. */
  factLabel: {
    width: 96,
  },
  line: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 2,
  },
  total: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
    paddingTop: spacing.sm,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  seller: {
    gap: 2,
  },
  note: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingTop: spacing.xs,
  },
  grow: {
    flex: 1,
  },
});

export default InvoiceScreen;
